import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { foldersAtom, fetchFoldersAtom, createFolderAtom } from '../../../../atoms/v3/folderAtoms';
import { projectsWithStatsAtom } from '../../../../atoms/v3/projectV2Atoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { FolderEditModal } from './FolderEditModal';
import { getFolderProjectStats } from '../../../../utils/folderUtils';

interface FolderSelectorProps {
  value: string | null;
  onChange: (folderId: string | null) => void;
  className?: string;
}

/**
 * 文件夹选择器组件
 * 支持选择现有文件夹、"无文件夹"选项、快速创建新文件夹
 */
export const FolderSelector: React.FC<FolderSelectorProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const { t } = useTranslation(['v3/projects', 'v3/common']);
  const [isDark] = useAtom(isDarkAtom);
  const [folders] = useAtom(foldersAtom);
  const [projectsWithStats] = useAtom(projectsWithStatsAtom);
  const [, fetchFolders] = useAtom(fetchFoldersAtom);
  const [, createFolder] = useAtom(createFolderAtom);

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // 组件挂载时获取文件夹列表
  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // 获取当前选中文件夹的显示名称
  const getDisplayName = () => {
    if (!value) {
      return t('v3/projects:folderSelector.noFolder', '无文件夹');
    }
    const folder = folders.find(f => f.id === value);
    return folder ? folder.name : t('v3/projects:folderSelector.unknownFolder', '未知文件夹');
  };

  // 处理文件夹选择
  const handleFolderSelect = (folderId: string | null) => {
    console.log('FolderSelector: 选择文件夹，folderId =', folderId);
    onChange(folderId);
    setIsOpen(false);
  };

  // 处理创建新文件夹
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const newFolder = await createFolder({ name: newFolderName.trim() });
      if (newFolder) {
        console.log('FolderSelector: 创建新文件夹成功，folderId =', newFolder.id);
        onChange(newFolder.id);
        setNewFolderName('');
        setIsCreating(false);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('创建文件夹失败:', error);
    }
  };

  // 处理取消创建
  const handleCancelCreate = () => {
    setNewFolderName('');
    setIsCreating(false);
  };

  // 处理编辑文件夹
  const handleEditFolder = (folder: any, event: React.MouseEvent) => {
    event.stopPropagation();
    const stats = getFolderProjectStats(projectsWithStats, folder.id);
    setEditingFolder({ ...folder, projectCount: stats.total });
    setIsEditModalOpen(true);
    setIsOpen(false);
  };

  // 关闭编辑模态框
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingFolder(null);
  };

  return (
    <div className={`relative ${className}`}>
      {/* 选择器按钮 */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full h-10 px-3 text-left rounded-ios-md text-ios-body focus:outline-none flex items-center justify-between
          ${isDark 
            ? 'bg-ios-dark-gray4 text-ios-dark-gray1' 
            : 'bg-ios-gray5 text-ios-gray1'
          }
        `}
      >
        <span className="truncate">{getDisplayName()}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* 下拉选项 */}
      {isOpen && (
        <div className={`
          absolute top-full left-0 right-0 mt-1 rounded-ios-md shadow-ios z-50 max-h-60 overflow-y-auto
          ${isDark 
            ? 'bg-ios-dark-gray5 border border-ios-dark-gray4' 
            : 'bg-white border border-ios-gray5'
          }
        `}>
          {/* 无文件夹选项 */}
          <button
            type="button"
            onClick={() => handleFolderSelect(null)}
            className={`
              w-full px-3 py-2 text-left text-ios-body hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 transition-colors
              ${!value ? 'bg-ios-blue/10 text-ios-blue dark:bg-ios-dark-blue/10 dark:text-ios-dark-blue' : ''}
            `}
          >
            {t('v3/projects:folderSelector.noFolder', '无文件夹')}
          </button>

          {/* 分割线 */}
          {folders.length > 0 && (
            <div className={`h-px mx-2 ${isDark ? 'bg-ios-dark-gray4' : 'bg-ios-gray5'}`} />
          )}

          {/* 现有文件夹列表 */}
          {folders.map((folder) => {
            const stats = getFolderProjectStats(projectsWithStats, folder.id);
            return (
              <div
                key={folder.id}
                className={`
                  flex items-center justify-between px-3 py-2 hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 transition-colors
                  ${value === folder.id ? 'bg-ios-blue/10 text-ios-blue dark:bg-ios-dark-blue/10 dark:text-ios-dark-blue' : ''}
                `}
              >
                <button
                  type="button"
                  onClick={() => handleFolderSelect(folder.id)}
                  className="flex-1 flex items-center gap-2 text-left text-ios-body truncate"
                >
                  <span>📁</span>
                  <span className="truncate">{folder.name}</span>
                  <span className={`
                    text-ios-caption px-1.5 py-0.5 rounded-full
                    ${isDark
                      ? 'bg-ios-dark-gray4 text-ios-dark-gray2'
                      : 'bg-ios-gray5 text-ios-gray2'
                    }
                  `}>
                    {stats.total}
                  </span>
                </button>

                {/* 编辑按钮 */}
                <button
                  type="button"
                  onClick={(e) => handleEditFolder(folder, e)}
                  className="ml-2 p-1 rounded text-ios-gray2 dark:text-ios-dark-gray2 hover:text-ios-gray1 dark:hover:text-ios-dark-gray1 hover:bg-ios-gray5 dark:hover:bg-ios-dark-gray3 transition-colors"
                  title={t('v3/common:actions.edit', '编辑')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
              </div>
            );
          })}

          {/* 分割线 */}
          <div className={`h-px mx-2 ${isDark ? 'bg-ios-dark-gray4' : 'bg-ios-gray5'}`} />

          {/* 新建文件夹区域 */}
          {isCreating ? (
            <div className="p-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder={t('v3/projects:folderSelector.newFolderPlaceholder', '文件夹名称')}
                  className={`
                    flex-1 px-2 py-1 text-ios-caption rounded border focus:outline-none
                    ${isDark 
                      ? 'bg-ios-dark-gray6 border-ios-dark-gray4 text-ios-dark-gray1' 
                      : 'bg-white border-ios-gray5 text-ios-gray1'
                    }
                  `}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateFolder();
                    } else if (e.key === 'Escape') {
                      handleCancelCreate();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                  className="px-2 py-1 text-ios-caption text-ios-blue dark:text-ios-dark-blue disabled:opacity-50"
                >
                  {t('v3/common:actions.create', '创建')}
                </button>
                <button
                  type="button"
                  onClick={handleCancelCreate}
                  className="px-2 py-1 text-ios-caption text-ios-gray dark:text-ios-dark-gray"
                >
                  {t('v3/common:actions.cancel', '取消')}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              className="w-full px-3 py-2 text-left text-ios-body text-ios-blue dark:text-ios-dark-blue hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 transition-colors"
            >
              + {t('v3/projects:folderSelector.createFolder', '新建文件夹')}
            </button>
          )}
        </div>
      )}

      {/* 点击外部关闭下拉框 */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false);
            setIsCreating(false);
            setNewFolderName('');
          }}
        />
      )}

      {/* 文件夹编辑模态框 */}
      <FolderEditModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        folder={editingFolder}
        projectCount={editingFolder?.projectCount || 0}
      />
    </div>
  );
};
